import React, { useEffect, useState } from "react";
import { Shuffle, Star } from "lucide-react";
import { patchFieldMappingTarget } from "../../utils/FieldMappingUtils";
import { Card, Text, Toggle } from "@merge-api/merge-javascript-shared";

const getLucideIconForOption = (iconName: String) => {
  switch (iconName) {
    case "shuffle":
      return <Shuffle size={16} />;
    case "star":
      return <Star size={16} />;
    default:
      return <Star size={16} />;
  }
};

type FieldMappingOptionProps = {
  title: String;
  subtitle: String;
  icon: String;
  isEnabled: boolean;
  disableSwitch?: boolean;
  handleChange: () => void;
  isDisabled: boolean;
};

const FieldMappingLinkOption = ({
  title,
  subtitle,
  icon,
  isEnabled,
  disableSwitch = false,
  handleChange,
  isDisabled,
}: FieldMappingOptionProps) => {
  return (
    <div className={`${isDisabled ? "opacity-50" : ""} flex items-start gap-x-4`}>
      <div className="pt-1">
        <Toggle
          checked={isEnabled}
          disabled={disableSwitch || isDisabled}
          onChange={handleChange}
        />
      </div>
      <div className="flex flex-col">
        <div className="flex flex-row items-center gap-x-[6px]">
          {getLucideIconForOption(icon)}
          <Text variant="h6">{title}</Text>
        </div>
        <Text variant="sm" className="text-gray-40">
          {subtitle}
        </Text>
      </div>
    </div>
  );
};

type FieldMappingLinkConfigurationOptionsProps = {
  fieldMappingTarget: any;
  isDisabled?: boolean;
};

const FieldMappingLinkConfigurationOptions = ({
  fieldMappingTarget,
  isDisabled = false,
}: FieldMappingLinkConfigurationOptionsProps) => {
  const [isMappableInLink, setIsMappableInLink] = useState(fieldMappingTarget?.is_mappable_in_link);
  const [isPrioritizedInLink, setIsPrioritizedInLink] = useState(fieldMappingTarget?.weight > 0);

  useEffect(() => {
    setIsMappableInLink(fieldMappingTarget?.is_mappable_in_link);
    setIsPrioritizedInLink(fieldMappingTarget?.weight > 0);
  }, [fieldMappingTarget]);

  const handleMappableToggle = () => {
    const newMappableValue = !isMappableInLink;
    // If turning mappable off, also turn prioritized off
    const newPrioritizedValue = newMappableValue ? isPrioritizedInLink : false;

    setIsMappableInLink(newMappableValue);
    if (!newMappableValue) {
      setIsPrioritizedInLink(false);
    }
    patchFieldMappingTarget({
      body: {
        is_mappable_in_link: newMappableValue,
        weight: newPrioritizedValue ? 1 : 0,
      },
      fieldMappingTargetId: fieldMappingTarget.id,
      onResponse: () => {},
      onError: () => {
        // Revert UI state on error
        setIsMappableInLink(!newMappableValue);
        setIsPrioritizedInLink(fieldMappingTarget?.weight > 0);
      },
    });
  };

  const handlePriorityToggle = () => {
    const newPrioritizedValue = !isPrioritizedInLink;

    setIsPrioritizedInLink(newPrioritizedValue);
    patchFieldMappingTarget({
      body: {
        is_mappable_in_link: isMappableInLink,
        weight: newPrioritizedValue ? 1 : 0,
      },
      fieldMappingTargetId: fieldMappingTarget.id,
      onResponse: () => {},
      onError: () => {
        // Revert UI state on error
        setIsPrioritizedInLink(!newPrioritizedValue);
      },
    });
  };

  const FieldMappinginLinkConfigOptions = [
    {
      title: "Mappable in Merge Link",
      subtitle: "If enabled, this target field will be editable by your users in Merge Link",
      icon: "shuffle",
      isEnabled: isMappableInLink,
      handleChange: handleMappableToggle,
    },
    {
      title: "Prioritized in Merge Link",
      subtitle:
        "If enabled, this target field will appear first in Merge Link along with other prioritized fields",
      icon: "star",
      isEnabled: isPrioritizedInLink,
      handleChange: handlePriorityToggle,
    },
  ];

  return (
    <Card className="flex flex-col py-5 px-6 gap-y-5" size="lg">
      <Text variant="h5">Configure field in Merge Link</Text>
      {FieldMappinginLinkConfigOptions.map((option) => {
        const isPrioritizedInLink = option.title.includes("Prioritized");
        return (
          <FieldMappingLinkOption
            title={option.title}
            subtitle={option.subtitle}
            icon={option.icon}
            isEnabled={option.isEnabled}
            disableSwitch={isPrioritizedInLink && !isMappableInLink}
            handleChange={option.handleChange}
            isDisabled={isDisabled}
          />
        );
      })}
    </Card>
  );
};

export default FieldMappingLinkConfigurationOptions;
